/** @jsx jsx */
import { Flex, Status, TabButton } from "@eworkit/iris";
import React, { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RoutedTabs } from "router";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { TimeReport } from "@/data/time-reports";
import { router } from "@/views/router";

export interface TimeReportDetailsTabsProps {
  timeReport: TimeReport;
}

export const TimeReportDetailsTabs: FC<TimeReportDetailsTabsProps> = React.memo(({ timeReport }) => {
  const { t } = useTranslation("session");
  const { hasCompanyManagerPrivileges } = useContext(UserContext);

  const calendar = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`time-reports.details.calendar`, { timeReportGuid: timeReport.guid }).path}>
        {t("TIME_REPORTS.TABS.CALENDAR")}
      </TabButton>
    ),
    [t, timeReport],
  );

  const specification = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`time-reports.details.specification`, { timeReportGuid: timeReport.guid }).path}>
        {t("TIME_REPORTS.TABS.SPECIFICATION")}
      </TabButton>
    ),
    [t, timeReport],
  );

  const summary = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`time-reports.details.summary`, { timeReportGuid: timeReport.guid }).path}>
        {t("TIME_REPORTS.TABS.SUMMARY")}
      </TabButton>
    ),
    [t, timeReport],
  );

  const lifecycle = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`time-reports.details.lifecycle`, { timeReportGuid: timeReport.guid }).path}>
        {t("TIME_REPORTS.TABS.LIFECYCLE")}
      </TabButton>
    ),
    [t, timeReport],
  );

  const versions = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`time-reports.details.versions`, { timeReportGuid: timeReport.guid }).path}
        placeholderRight={<Status size="sm">{timeReport.versionCount}</Status>}>
        {t("TIME_REPORTS.TABS.VERSIONS")}
      </TabButton>
    ),
    [t, timeReport],
  );

  const related = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`time-reports.details.related`, { timeReportGuid: timeReport.guid }).path}
        placeholderRight={<Status size="sm">{timeReport.relatedEntities.totalCount}</Status>}>
        {t("TIME_REPORTS.TABS.RELATED")}
      </TabButton>
    ),
    [t, timeReport],
  );

  const tabs = useMemo(
    () =>
      hasCompanyManagerPrivileges()
        ? {
            summary,
            calendar,
            specification,
            lifecycle,
            versions,
            related,
          }
        : {
            calendar,
            specification,
            summary,
            lifecycle,
            versions,
          },
    [hasCompanyManagerPrivileges, summary, calendar, specification, lifecycle, versions, related],
  );

  return (
    <Flex flexDirection="row" alignItems="flex-end" sx={{ overflowX: "auto" }}>
      <RoutedTabs routeParam="tab" views={tabs} />
    </Flex>
  );
});
